// V2 版式：gantt（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/gantt.ts 逐坐标移植
// 左标签列 + 离散刻度竖网格 + 每任务一条横条（唯一共享 value→x 映射）；
// lanes ≤4 泳道分组带 + 道名。后端对出轴/行高不足 loud 失败；
// 前端预览对非法数据做 clamp 容错（编辑器中间态不能崩页），正常数据下与后端几何一致。
// 几何取 V2GEO["gantt"]（deno dump 同源，勿手改数值）。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

// deno dump 同源：与后端 gantt.ts GEO 一一对应
const GEO = V2GEO["gantt"] as any;

// gantt block 结构以后端 _shared/pptx/types.ts 为准（slides.ts Block 已同步）
type GanttTask = { label: string; start: number; end: number; lane?: number };
type GanttBlock = { type: "gantt"; ticks: string[]; lanes?: string[]; tasks: GanttTask[] };

export default function V2Gantt({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const m = SPACING.marginX;
  const W = 13.333;

  const gi = c.blocks.findIndex((b) => (b as { type: string }).type === "gantt");
  const gb = gi >= 0 ? (c.blocks[gi] as unknown as GanttBlock) : null;
  const ticks = (gb ? gb.ticks : []).slice(0, 6);
  const nTicks = Math.max(ticks.length, 2); // 前端容错：不足 2 刻度按 2 处理（后端 loud 失败）
  const lanes = (gb?.lanes ?? []).slice(0, 4);
  const tasks = (gb ? gb.tasks : []).slice(0, 9);

  // ---- 唯一共享 value→x 映射（刻度序号 → x 中心，与后端同公式）----
  const gridX = m + GEO.labelW + GEO.labelGap;
  const gridW = W - m - gridX;
  const xOf = (v: number) => gridX + (gridW * v) / (nTicks - 1);
  // 前端容错 clamp（后端对出轴/泳道索引 loud 失败——预览阶段不崩页）
  const cl = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

  // ---- 纵向预算：头部 + 泳道头×m + 任务行高×n（与后端同公式）----
  const nLane = lanes.length > 0 ? lanes.length : 1;
  const headTotal = GEO.tickH + GEO.laneHeadH * nLane;
  const plotH = GEO.chart.bottom - GEO.chart.top - headTotal;
  const perLane = plotH / nLane;
  const tasksInLane = (li: number) =>
    lanes.length > 0 ? tasks.filter((t) => (t.lane ?? 0) === li) : tasks;
  const maxInLane = lanes.length > 0
    ? Math.max(...lanes.map((_, li) => tasksInLane(li).length))
    : tasks.length;
  const rowH = maxInLane > 0 ? (perLane - (maxInLane - 1) * 0.12) / maxInLane : perLane;
  const barH = rowH * GEO.barHRatio;

  const tickTop = GEO.chart.top;

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "gantt")} page={page} total={total} foot={footOf()} />

      {/* 页面标题（长度分档） */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(GEO.title.x), top: px(GEO.title.y),
          width: px(GEO.title.w), height: px(GEO.title.h),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: skin.serifHead ? 700 : 400, lineHeight: 1.15,
        }}
      />

      {/* 刻度标签 + 竖网格 */}
      {ticks.map((tk, i) => {
        const x = xOf(i);
        return (
          <div key={`t${i}`}>
            <EditableText
              value={tk} path={`blocks.${gi}.ticks.${i}`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(x - 0.7), top: px(tickTop),
                width: px(1.4), height: px(GEO.tickH),
                fontSize: pt(skin.scale.meta), color: skin.sub, fontFamily: skin.fontMono,
                textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center",
              }}
            />
            {i > 0 && (
              <div style={{
                position: "absolute", left: px(x), top: px(tickTop + GEO.tickH),
                width: 1, height: px(GEO.chart.bottom - tickTop - GEO.tickH),
                background: skin.hairline, opacity: 0.6,
              }} />
            )}
          </div>
        );
      })}

      {/* 泳道分组带 + 道名 */}
      {lanes.map((ln, li) => {
        const laneTop = tickTop + GEO.tickH + li * (GEO.laneHeadH + perLane);
        return (
          <div key={`l${li}`}>
            <div style={{
              position: "absolute", left: px(gridX), top: px(laneTop + GEO.laneHeadH),
              width: px(gridW), height: px(perLane),
              background: skin.blush, borderRadius: px(skin.radius),
            }} />
            <EditableText
              value={ln} path={`blocks.${gi}.lanes.${li}`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(gridX), top: px(laneTop),
                width: px(gridW), height: px(GEO.laneHeadH),
                fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontBody,
                fontWeight: 700, display: "flex", alignItems: "center",
              }}
            />
          </div>
        );
      })}

      {/* 任务条（按泳道分组 + 区内顺序） */}
      {tasks.map((t, ti) => {
        const li = lanes.length > 0 ? cl(t.lane ?? 0, 0, lanes.length - 1) : 0;
        const laneTasks = tasksInLane(li);
        const order = laneTasks.indexOf(t);
        const laneTop = tickTop + GEO.tickH + li * (GEO.laneHeadH + perLane) + GEO.laneHeadH;
        const y = laneTop + order * rowH + (rowH - barH) / 2;
        const x1 = xOf(cl(t.start, 0, nTicks - 1));
        const x2 = xOf(cl(t.end, 0, nTicks - 1));
        const w = Math.max(x2 - x1, 0.08);
        // 任务名：条内放不下则放条右外侧（与后端同规则）
        const labelFit = w > 0.9;
        return (
          <div key={ti}>
            <div style={{
              position: "absolute", left: px(x1), top: px(y),
              width: px(w), height: px(barH),
              background: skin.accent, borderRadius: px(Math.min(skin.radius, barH / 2)),
            }} />
            <EditableText
              value={t.label} path={`blocks.${gi}.tasks.${ti}.label`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute",
                left: px(labelFit ? x1 : x2 + 0.08),
                top: px(laneTop + order * rowH),
                width: px(labelFit ? w : gridX + gridW - x2 - 0.08), height: px(rowH),
                fontSize: pt(skin.scale.meta),
                color: labelFit ? skin.accentOn : skin.text, fontFamily: skin.fontBody,
                textAlign: labelFit ? "center" : "left",
                display: "flex", alignItems: "center",
                justifyContent: labelFit ? "center" : "flex-start",
                whiteSpace: "nowrap",
              }}
            />
          </div>
        );
      })}
    </>
  );
}
